import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { SlidersHorizontal, ChevronDown, Check } from 'lucide-react';

export default function CategoryFilterDropdown({ categories, value, onChange }) {
  const [open, setOpen]   = useState(false);
  const [pos,  setPos]    = useState(null);
  const btnRef  = useRef(null);
  const menuRef = useRef(null);

  /* Calculate position from the button's bounding rect */
  const calcPos = useCallback(() => {
    if (!btnRef.current) return null;
    const r = btnRef.current.getBoundingClientRect();
    return { top: r.bottom + 6, left: r.right };   // right-edge anchor
  }, []);

  const handleToggle = () => {
    if (!open) setPos(calcPos());   // set position before first render
    setOpen(o => !o);
  };

  /* Re-position on scroll while open */
  useEffect(() => {
    if (!open) return;
    const onScroll = () => setPos(calcPos());
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open, calcPos]);

  /* Close on outside click */
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (
        btnRef.current  && !btnRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (!categories || categories.length < 1) return null;

  const menu = pos && open && createPortal(
    <div
      ref={menuRef}
      style={{
        position : 'fixed',
        top      : pos.top,
        left     : pos.left,
        transform: 'translateX(-100%)',   // right-align with button edge
        zIndex   : 9999,
      }}
      className="bg-white border border-stone-200 rounded-xl shadow-xl py-1 min-w-[180px] max-h-64 overflow-y-auto"
    >
      {[null, ...categories].map(cat => (
        <button
          key={cat ?? '__all__'}
          data-testid={`filter-option-${cat ?? 'all'}`}
          onClick={() => { onChange(cat); setOpen(false); }}
          className={`w-full flex items-center justify-between px-4 py-2 text-sm font-nunito transition-colors ${
            value === cat
              ? 'text-[#004B00] font-semibold bg-green-50'
              : 'text-gray-700 hover:bg-stone-50'
          }`}
        >
          {cat ?? 'All Categories'}
          {value === cat && <Check size={13} className="text-[#004B00]" />}
        </button>
      ))}
    </div>,
    document.body
  );

  return (
    <>
      <button
        ref={btnRef}
        onClick={handleToggle}
        data-testid="category-filter-btn"
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-nunito font-semibold transition-all ${
          value
            ? 'bg-[#004B00] text-white border-[#004B00]'
            : 'bg-white/80 text-gray-700 border-white/60 hover:border-green-300 hover:text-[#004B00]'
        }`}
      >
        <SlidersHorizontal size={14} />
        {value || 'Filter'}
        <ChevronDown size={13} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {menu}
    </>
  );
}
